import { useQuery } from '@apollo/client'
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { AreasSelector } from '@rankmi/rankmi-table'
import { Divider } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { Link } from 'react-router-dom'
import Loader, { LoaderSizes } from 'components/ui/loader/Loader'
import Breadcrumb, { BreadcrumbItem } from 'design-system/breadcrumb'
import { createRouteExternalCourse, PathRoutes } from 'enum/routes'
import { sortTableEnum } from 'enum/sortTable.enum'
import {
  EXTERNAL_COURSE_USERS,
  QueryExternalCourseUsersArgs,
  TDataExternalCourseUsers,
} from 'graphqlAPI'
import { applyFilters } from 'helpers/filterData.helper'
import {
  useTranslationRankmi,
  generateFilterViewerLocale,
} from 'helpers/translations.helper'
import * as fromAreaActions from 'store/actions/areas.action'
import { Actions as ActionsExternalCourses } from 'store/actions/externalCourses.action'
import { IMainState } from 'store/states'
import { IAreaModel } from 'models/area.model'
import { CourseData } from 'components/ExternalCourses/CourseData'
import { prefixTranslation } from './common'
import { Table, Buttons } from './components'
import { useFiltersColumns } from './hooks'
import styles from './ExternalCourse.module.scss'
import Wrapper from 'components/reusables/wrapper'

export const ExternalCourse = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { loading: loadingExternalCourse, data: externalCourse } = useSelector(
    ({ externalCourses }: IMainState) => externalCourses.externalCourse
  )

  const { id } = useParams<Record<'id', string>>()
  const { areas, loading: areasLoading } = useSelector(
    ({ areas }: IMainState) => areas
  )
  const [area, setArea] = useState<IAreaModel | null>(null)

  let bounceId: any
  const [searchText, setSearchText] = useState('')
  const [selectedFilters, setSelectedFilters] = useState({} as any)
  const [page, setPage] = useState(1)
  const [sorts, setSorts] = useState<string[]>([])
  const [perPage, setPerPage] = useState(10)
  const [selected, setSelected] = useState<number[]>([])
  const [allSelected, setAllSelected] = useState(false)
  const filtersColumns = useFiltersColumns()
  const [translations, setTranslations] = useState<any>()
  const tRelative = useTranslationRankmi({
    prefix: prefixTranslation,
  })

  useEffect(() => {
    setTranslations(() => generateFilterViewerLocale())
    dispatch({ type: fromAreaActions.GET_AREAS_LIST })
  }, [dispatch])

  useEffect(() => {
    dispatch(ActionsExternalCourses.getAdminExternalCourse({ id }))
  }, [dispatch, id])

  const handleSearch = (value: string) => {
    if (bounceId) clearTimeout(bounceId)
    bounceId = setTimeout(() => {
      setSearchText(value.trim())
    }, 1000)
  }

  const q = useMemo(
    () => ({
      user_full_name_or_user_identifier_or_user_position_cont: searchText,
      sorts,
      g: applyFilters(selectedFilters),
    }),
    [searchText, selectedFilters, sorts]
  )

  const queryExternalCourseUsers = useQuery<
    TDataExternalCourseUsers,
    QueryExternalCourseUsersArgs
  >(EXTERNAL_COURSE_USERS, {
    variables: {
      externalCourseId: id,
      page,
      perPage,
      areaId: area?.id.toString(),
      q,
    },
  })

  const { data: dataExternalCourseUsers } = queryExternalCourseUsers

  const { externalCourseUsers } = { ...dataExternalCourseUsers }
  const { nodesCount = 0 } = { ...externalCourseUsers }

  return (
    <Wrapper shadow={true}>
      <div style={{ marginTop: -7 }}>
        <Breadcrumb
          separator={
            <FontAwesomeIcon
              className={styles.breadcrumbSeparator}
              icon={faChevronRight}
            />
          }
        >
          <Link
            component={(props: any) => (
              <BreadcrumbItem
                text={tRelative('breadcrumb')}
                onClick={(e) => {
                  e.preventDefault()
                  props?.navigate()
                }}
                href={props.href}
              />
            )}
            to={PathRoutes.MONITORING_COURSES}
          />
          <BreadcrumbItem
            href={createRouteExternalCourse({ id })}
            text={
              externalCourse?.name || (
                <Loader
                  classes={{ loader: 'p-0' }}
                  sizes={LoaderSizes.ExtraSmall}
                  color
                />
              )
            }
            active
          />
        </Breadcrumb>
      </div>
      {loadingExternalCourse && <Loader sizes={LoaderSizes.ExtraSmall} color />}
      {!loadingExternalCourse && !!externalCourse && (
        <>
          <CourseData data={externalCourse} />
          <Row>
            <Col xs={12}>
              <Divider />
            </Col>
            <Col xs={12}>
              <h1 className="font-weight-bold">
                {tRelative('statistics.title')}
              </h1>
            </Col>
            <Col
              xs={12}
              md={2}
              className="d-flex flex-column justify-content-center"
            >
              <span className={styles.title}>{t('monitoring.title')}</span>
              <AreasSelector
                areas={areas}
                loading={areasLoading}
                selectedArea={area || undefined}
                mandatoryArea={false}
                onAreaSelected={(area: IAreaModel) => {
                  setArea(+area.id === 0 ? null : area)
                }}
              />
            </Col>
            <Col xs={12} className="mt-4">
              <Buttons
                {...{
                  id,
                  filtersColumns,
                  onChangeFilters: (filters: IAreaModel) => {
                    setSelectedFilters(filters)
                  },
                  allSelected,
                  selected,
                  total: nodesCount,
                  area,
                  translations,
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Table
                data={externalCourseUsers?.nodes || []}
                loading={queryExternalCourseUsers.loading}
                total={externalCourseUsers?.nodesCount || 0}
                setSearch={handleSearch}
                setPage={setPage}
                setSort={({ field, order }) => {
                  setSorts(order ? [`${field} ${sortTableEnum[order]}`] : [])
                }}
                setPerPage={setPerPage}
                setSelectedRows={setSelected}
                selectedRows={selected}
                setAllSelected={setAllSelected}
              />
            </Col>
          </Row>
        </>
      )}
    </Wrapper>
  )
}

export default ExternalCourse
